import { h, Host, forceUpdate } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations from "../../utils/loadTranslations";
import Fragment from "../../utils/Fragment";
import { createRequestData } from "../../api/publicApi/createCase/createCase";
import { getPublicApiClient } from "../../api/apiClient/publicApiClient";
import { getEnvConfig, setEnv } from "../../api/config";
import { StepBack, StepNavigation, StepNext, FormStep, FormSteps } from "./steps";
import { createApiStore, loadFormDataFromAssetManagementApi, loadFormDataFromPublicApi } from "./stores/apiStore";
import { createAuthStore, SilentAuth, whenAuthenticated } from "../../utils/auth";
import { createFormStore } from "./stores/formStore";
import { ErrorNumberInput } from "./form-elements/error-number-input";
import { EmailInput, FirstNameInput, LastNameInput, PhoneInput, SalutationInput } from "./form-elements/contact-inputs";
import { InstallationCheckbox, LicenseCheckbox, SoftwareClassificationSelect, SoftwareModuleSelect, SoftwareTypeSelect } from "./form-elements/software-inputs";
import { ApplicationIssueSelect, ApplicationOpticsSelect, ApplicationSelect } from "./form-elements/application-inputs";
import { MachineSelect } from "./form-elements/machine-select";
import { NotificationTypeSelect } from "./form-elements/notification-type-select";
import { getAssetManagementApiClient } from "../../api/apiClient/assetManagementApiClient";
import { DescriptionInput } from "./form-elements/description-input";
/**
 * @slot contacts-button - Slot for native contacts button to select contact data from phone address book
 * @slot connector - Slot for the connector that handles sending the form data to a backend
 */
export class MfCreateServiceCase {
    constructor() {
        /**
         * If true, the form will be displayed in multiple steps
         */
        this.multipleSteps = false;
        /**
         * Enable file upload in the form
         */
        this.fileUpload = false;
        /**
         * Data that should be prefilled in the form
         * @optional
         */
        this.prefillData = null;
        /**
         * Application name that will be used as origin of the source when sending the form.
         * This can be overridden by the prefillData.origin
         */
        this.origin = "";
        /**
         * The locale to use for translations and the phone input
         */
        this.locale = "en-US";
        /**
         * Environment string to decide which endpoints to use
         */
        this.env = "prod";
        /**
         * use onprem Public API instead of azure
         * @deprecated only used until April 2025
         */
        this.legacyApi = false;
        /**
         * disable silent auth - then the application needs to supply the auth token via @trumpf/ux.utils
         * @optional
         */
        this.disableSilentAuth = false;
        this.tbdbError = null;
        this.apiStore = createApiStore();
        this.authStore = createAuthStore();
        this.formStore = createFormStore();
        this.callId = crypto.randomUUID(); // this browser API only works on localhost and over https
    }
    connectedCallback() {
        setEnv(`${this.env}${this.legacyApi ? "Legacy" : ""}`);
        this.config = getEnvConfig();
        this.publicApiClient = getPublicApiClient(this.config);
        this.unsubscribeToTbdbToken = this.publicApiClient.subscribeToTbdbToken((event) => {
            if (event.success) {
                loadFormDataFromPublicApi(this.apiStore, this.publicApiClient);
            }
            else {
                this.tbdbError = event.errorMessage || "Error getting TBDB token";
            }
        });
        this.assetManagementApiClient = getAssetManagementApiClient(this.config);
        this.loadTranslations();
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.unsubscribeAuth = whenAuthenticated(() => loadFormDataFromAssetManagementApi(this.apiStore, this.assetManagementApiClient));
        this.apiStore.onChange("notificationTypesLoading", (notificationTypesLoading) => {
            var _a;
            if (!notificationTypesLoading) {
                this.formStore.state.selectedNotificationType = ((_a = this.prefillData) === null || _a === void 0 ? void 0 : _a.notificationType) || null;
            }
        });
    }
    disconnectedCallback() {
        this.unsubscribeAuth();
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        if (this.unsubscribeToTbdbToken) {
            this.unsubscribeToTbdbToken();
        }
    }
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }
    onPrefillDataChange() {
        var _a;
        const notificationType = (_a = this.prefillData) === null || _a === void 0 ? void 0 : _a.notificationType;
        if (notificationType) {
            this.formStore.state.selectedNotificationType = notificationType;
        }
    }
    loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }
    onSubmit(event) {
        var _a;
        event.preventDefault();
        const { softwares } = this.apiStore.state;
        const { form, callId, origin, formStore } = this;
        const data = createRequestData({
            form,
            callId,
            softwares,
            origin: ((_a = this.prefillData) === null || _a === void 0 ? void 0 : _a.origin) || origin,
            selectedMachineTechnology: formStore.state.selectedMachineTechnology
        });
        const files = this.fileUpload
            ? Array.from(form.querySelectorAll("input[type='file']")).reduce((acc, input) => {
                return acc.concat(Array.from(input.files || []));
            }, [])
            : [];
        this.serviceCaseSubmit.emit({ data, callId, files });
        formStore.state.formIsSubmitted = true;
        if (this.config.logging) {
            // eslint-disable-next-line no-console
            console.log("Form submitted", data);
        }
    }
    onCancel() {
        this.serviceCaseCancel.emit();
    }
    render() {
        const t = (key, options = undefined) => i18nProvider.t(`mf-service-calls:${String(key)}`, options);
        const { isAllowedToCreateCase, loading } = this.apiStore.state;
        const { isAuthenticating, authError } = this.authStore.state;
        const { prefillData, multipleSteps } = this;
        const { showErrorNumbers, formIsSubmitted, showApplications, showSoftwareModules, showMachines, showSoftwareFields, showWeldingApplicationFields } = this.formStore.state;
        const showContent = isAllowedToCreateCase && !(loading || isAuthenticating || authError);
        return (h(Host, { key: 'e811d634a41a348072864ab988994ec811abc7f2' }, !formIsSubmitted && (h("ux-form", { key: '5f991968ee047243355c0aa1a91013c690d079cc', "data-validation-required--msg": t("validation.requiredField") }, h(FormSteps, { key: 'fbd3189ccca81b726b2a96b83f80d67e1336f486', multipleSteps: multipleSteps }, h("form", { key: '4c4fc8c632d6b33f04715339c82bc78722d96f15', onSubmit: (e) => this.onSubmit(e), ref: (el) => (this.form = el) }, h("div", { key: '0959d0e5c694ff35d15301c35aa8027a21fbbf8e', class: "ux-u-text-align--center ux-u-margin-top-60 ux-u-margin-bottom-60" }, h("div", { key: '05624099f9afca58a08a8e2f452c1a981bff02dc', class: "ux-text-headline ux-text-headline--giant" }, t("headline.createCase"))), (loading || isAuthenticating) && (h("ux-loader", { key: '200014066b9602fc05ca91364189fe6683e21a99', class: "ux-u-margin-bottom-40", variant: "spinner" })), authError && (h("div", { key: '7f5f682f9366baa450eff1dccded96fd32e44a2a', class: "ux-u-text-align--center ux-text-normal ux-u-margin-top-20 ux-u-margin-bottom-20" }, t("auth.error"))), isAllowedToCreateCase === false && (h("div", { key: '025a084dcb31b898bd61a9f1d09ecd250b635481', class: "ux-u-text-align--center ux-text-normal ux-u-margin-top-20 ux-u-margin-bottom-20" }, t("missingAuthorization"))), h("div", { key: 'f3b6047344293e7776b10822e65fcd5ff71e4dbf', style: { display: showContent ? "block" : "none" } }, h(FormStep, { key: '3bb29a4f0a41c698fab7078d7e200f8c1bf479ed', multipleSteps: multipleSteps }, h(StepNavigation, { key: '830c060c81e05ed12f9bbeed6a4e71f51162729f', t: t, multipleSteps: multipleSteps, currentStep: 1 }), h("div", { key: 'f5c7089841f72b442432df2dee4449d73fcb537f', class: "ux-row ux-u-flex-jc-center" }, h("div", { key: 'e18054eb2ad77b4158d806324671cca957ff6c2b', class: "ux-col-12 ux-col-md-10" }, h("div", { key: '0a7a9c222e959644db24c0c7f01ac4f8692ba2bf', class: "ux-row" }, h("div", { key: 'bf592d5d88a6b84ad060e972ad1e3f8ce3b9a50b', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'b30955637a478cb2dfe0f6b464354b2901610856', class: "ux-u-margin-bottom-20" }, h(NotificationTypeSelect, { key: '18dcf9599c1658649e0522010f95b07a1b675941', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))), showMachines && (h("div", { key: '00be714e4b009415afd871de014cc38a3cdf7acf', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'd8aea14a2b8c0b2bbbb11a8f7b0bd0e8922430c5', class: "ux-u-margin-bottom-20" }, h(MachineSelect, { key: '71b4ea21bceed13eadfb361450404901331c83c8', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))))), showApplications && (h(Fragment, { key: '75351e8fca7bcb0ec930db45d3dbebc5c20afb0e' }, h("div", { key: 'f446fa882eae55717bd1feba5b5865a8e399ba31', class: "ux-row" }, h("div", { key: 'c67b7a3aeaad939182ddcb1d200b23cfebc69e70', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '6df05df0debb75a6c84f839750f53142865dba13', class: "ux-u-margin-bottom-20" }, h(ApplicationSelect, { key: '0f89651731625c1ab8a7687af89bb9c8a0af4e47', t: t, formStore: this.formStore, prefillData: prefillData })))), showWeldingApplicationFields && (h("div", { key: '4a9e0c127797e2f157d5d5f8b9cd10e679a79756', class: "ux-row" }, h("div", { key: '7c44990322943d8d4f55f006c3f0a1feed8b5da5', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '7a77e51ebc6cb6e636db72b736b2a0b0ffa95e3a', class: "ux-u-margin-bottom-20" }, h(ApplicationOpticsSelect, { key: '671f9d05868b2c394acbbae8399cef6604b73a2f', t: t, prefillData: prefillData }))), h("div", { key: '1d8d56a500e0345b03495c3a61d08b8cc8a4dcd6', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'a8d1ac2b31f911bd2119f3bf85abc9c265b8ffc0', class: "ux-u-margin-bottom-20" }, h(ApplicationIssueSelect, { key: '1bfdb2d035758e99e2d46832ad6a95531738edd5', t: t, prefillData: prefillData }))))))), showSoftwareFields && (h(Fragment, { key: 'e152da47f461a9689ce611adb1e08e98f3517fe5' }, h("div", { key: '4562c7477ac464c67acc2f30cda9fb384e3fde35', class: "ux-row" }, h("div", { key: 'c7e1b4e5408a56fe8ee1b9830097b30df2609afc', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '32f09630d962f4a82b2905e5b89caedc565242de', class: "ux-u-margin-bottom-20" }, h(SoftwareClassificationSelect, { key: '5d3ccf717a95d7458c3ba4c387773edb5d89c956', t: t, prefillData: prefillData })))), h("div", { key: '7eb06205a01f875e49845a4731dfa45cbf2647c7', class: "ux-row" }, h("div", { key: '818ede4000fee334c08f05789cda45d586b62e83', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '76594a16d2a806e0fcb5fd553b56d6e40561b5ca', class: "ux-u-margin-bottom-20" }, h(InstallationCheckbox, { key: 'df8f435cdf7e95c8d3781b2668df6e823bd201ee', t: t, prefillData: prefillData }))), h("div", { key: '5f7cdd28163fc8f1affefe89d000e212866c8f8c', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '33a48c6df611ff1fcef6f7b9dd742beb6ba9077d', class: "ux-u-margin-bottom-20" }, h(LicenseCheckbox, { key: '02ac98c4fbf5129a9e92963838beedd4fdfec892', t: t, prefillData: prefillData })))), h("div", { key: 'abe91b4c50a08da50888176662a565438ea5ed64', class: "ux-row" }, h("div", { key: '5a66d655a30afa4e05132d29c92d36b377fba837', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'f346025e3da3ec0823ff2f51a18e6c03da8bdec3', class: "ux-u-margin-bottom-20" }, h(SoftwareTypeSelect, { key: 'd7e7f5f80d258de88985278f1880af4ab2fa5e67', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))), showSoftwareModules && (h("div", { key: '937bb548f8aa466ec4607c41329fa430dd3a4ff3', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'b1218048d5aa9200b66f3a52cd7083528daea271', class: "ux-u-margin-bottom-20" })))), h("div", { key: 'f355c3dda879deb96b583653abbc121c599eaf1c', class: "ux-row" }, showSoftwareModules && (h("div", { key: '3a4ac7f2ae51adb8e9899298c774ff3385fe1ee7', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '1a7a7e4f19a2da1acf6add7296970479e38928af', class: "ux-u-margin-bottom-20" }, h(SoftwareModuleSelect, { key: '7b3812809320d619054c040d6aa0f12cff00183b', t: t, prefillData: prefillData, formStore: this.formStore, apiStore: this.apiStore }))))))), showErrorNumbers && (h("div", { key: 'e028355bc75537ffdf385a9e6124e69b6f509332', class: "ux-row" }, h("div", { key: '79d4c0589918b18e72aa544912536d1cc6445fdf', class: "ux-col-12" }, h(ErrorNumberInput, { key: 'c4d374b6d479f23947ef17425476e8d2754098a1', t: t, formStore: this.formStore, prefillData: prefillData })))), h("div", { key: 'e0cc631210b7b9df221077b04e014f8753e13b67', class: "ux-row" }, h("div", { key: '8f041d40d3c63d1a8da481923e94354634d9c4be', class: "ux-col-12" }, h("div", { key: 'd512c277bcf2b24f32759adb43617c937305319b', class: "ux-u-margin-bottom-20" }, h(DescriptionInput, { key: '1c9f1d89079299bef7d704a8348cb8d68f346f4c', t: t, prefillData: prefillData })))), this.fileUpload && (h("div", { key: '6b3fcf41fb0c6796fe4d39fc53e86be6b05b3583', class: "ux-row" }, h("div", { key: 'e7ddd74a984cd75604d30e387845be071c6143b8', class: "ux-col-12" }, h("div", { key: 'c118a4f1a00f631fce36b49bc19e7cb340e0edae', class: "ux-u-margin-bottom-20" }, h("ux-file-upload", { key: '080884ae0b40071884d7fc90451c81c4f3e43e6b', "max-file-size": "50", label: t("field.fileUpload.label"), "accepted-files": "image/*,application/*,text/*,video/mp4,", "max-files": "4" }), h("p", { key: '7841c4fc9e4f93044fe19c9c34e3505f8d68908d', class: "ux-text-tiny" }, t("field.fileUpload.info")))))), h(StepNext, { key: '1267cd621ed8b090b073c575960fe53c0ca6e6ca', t: t, multipleSteps: multipleSteps, onCancel: () => this.onCancel() })))), h(FormStep, { key: '836c21d1f9c69c6b30ae579046e52184973dd566', multipleSteps: multipleSteps }, h(StepNavigation, { key: 'cf42d64aeb441a9cc8c973bb1744d8b1374aa661', t: t, multipleSteps: multipleSteps, currentStep: 2 }), h("div", { key: 'cb54c72ae321b0754ced11c943902b62bb9dab60', class: "ux-row ux-u-flex-jc-center" }, h("div", { key: '3d184809ff304f3604f77961e9adcc4c5ef4ce04', class: "fields-wrapper ux-col-12 ux-col-md-10" }, h("div", { key: '01e1ae5c0c5805b180531bda30ca9347c01cfb7a' }, h("slot", { key: '3b8d22ef14158ec03823bddb64de1e7eed1d2fa6', name: "contacts-button" })), h("div", { key: 'f6a8363b1ec818d25f05b07dce1dca6974d697b8', class: "ux-row" }, h("div", { key: '1431e7b5b839f6351153602ad1337071afd35b0f', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'ccc35b2ffbb636f1333011effb54f63c3fc9e1b3', class: "ux-u-margin-bottom-20" }, h(SalutationInput, { key: '547ffc0b424c39efd9de3dc1b7961f2b47f7ebce', t: t, apiStore: this.apiStore })))), h("div", { key: '53535a3673cf634f66b07aad44ad7845c28b3a0d', class: "ux-row" }, h("div", { key: 'a50472b6dd5ffefb4459d6e834d0ed0d85cb68bb', class: "ux-col-12 ux-col-md-6" }, h("div", { key: '2ceb0e5a5c045dfddb0b93dd8d068f48b8bf74e0', class: "ux-u-margin-bottom-20" }, h(FirstNameInput, { key: '15178c181d060f81f1bd1240d33683044547aeff', t: t, apiStore: this.apiStore }))), h("div", { key: 'dd2b9ae1d177e8fa227176080fc095511d8a2a88', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'ba9842e63f7518f38d5238d30671ccbf633a3674', class: "ux-u-margin-bottom-20" }, h(LastNameInput, { key: '326efc74ae06f93f54c072ea709d0e4eaab45dc6', t: t, apiStore: this.apiStore })))), h("div", { key: '9771c943930249e88fc649560f80eb4acb8eeee3', class: "ux-row" }, h("div", { key: '88955220a317d93362037f7989def24d49fa257e', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'f06af210fb9fdf51e5a8b267002e84274cb155dc', class: "ux-u-margin-bottom-20" }, h(PhoneInput, { key: '563e8190415bbd9ae70d97928dbdcd3079c13bb9', t: t, apiStore: this.apiStore, locale: this.locale }))), h("div", { key: '0d457f3b5745507deabf488fb591d61f390a172a', class: "ux-col-12 ux-col-md-6" }, h("div", { key: 'e06e2a7870e163010288fec3fb7833c6398d35b9', class: "ux-u-margin-bottom-20" }, h(EmailInput, { key: '2ec7d6c7fcf20f6d76cb9a20d3cf19a0808e7338', t: t, apiStore: this.apiStore })))), h("div", { key: '5144d9e13f6ed965eaf62edf6898bc4430edfe06', class: "ux-u-margin-bottom-20" }, h("div", { key: '22db5bbd2abf9b30f4edb589e9ae3150149a895c', class: "ux-text-normal ux-text-normal--light-gray" }, h("p", { key: 'c34237d3d9035905c4d577e02a7076997e990801' }, t("validation.requiredFieldsExplanation")))), h("div", { key: '4ccc07047876b95477e1aeb088393b350ffe51ef', class: "ux-u-d-flex ux-u-flex-jc-between ux-u-flex-dir-row-reverse" }, h("ux-button", { key: '5cc32ac95a6b2bf0badec0ed460978127075046d', type: "submit", variant: "primary" }, t("button.submit")), h(StepBack, { key: '4582c6501ba13821c5ba67855a3fd3ec88a633b0', t: t, multipleSteps: multipleSteps }), !multipleSteps && (h("ux-button", { key: '5002621012d0e33c2d2235e8ce023d9e6e433630', type: "button", onClick: () => this.onCancel(), formnovalidate: true }, t("button.cancel")))))))))))), h("div", { key: '1504c589f2fd47534871388ad76e82f7dc41da38', style: { display: formIsSubmitted ? "block" : "none" } }, h("slot", { key: 'd9bccdf953d1476a4cdd95b59ce717cd19eeda0e', name: "connector" })), h(SilentAuth, { key: '2f92a9f99b701178bfeb0957672b3907bfb7d391', config: this.config, disabled: this.disableSilentAuth, authStore: this.authStore })));
    }
    static get is() { return "mf-create-service-case"; }
    static get originalStyleUrls() {
        return {
            "$": ["create-service-case.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["create-service-case.css"]
        };
    }
    static get properties() {
        return {
            "multipleSteps": {
                "type": "boolean",
                "attribute": "multiple-steps",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If true, the form will be displayed in multiple steps"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "fileUpload": {
                "type": "boolean",
                "attribute": "file-upload",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Enable file upload in the form"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "prefillData": {
                "type": "unknown",
                "attribute": "prefill-data",
                "mutable": false,
                "complexType": {
                    "original": "PrefillData | null",
                    "resolved": "PrefillData | null",
                    "references": {
                        "PrefillData": {
                            "location": "import",
                            "path": "../../api/publicApi/createCase/createCase",
                            "id": "src/api/publicApi/createCase/createCase.ts::PrefillData"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "optional",
                            "text": undefined
                        }],
                    "text": "Data that should be prefilled in the form"
                },
                "getter": false,
                "setter": false,
                "defaultValue": "null"
            },
            "origin": {
                "type": "string",
                "attribute": "origin",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Application name that will be used as origin of the source when sending the form.\nThis can be overridden by the prefillData.origin"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"\""
            },
            "locale": {
                "type": "string",
                "attribute": "locale",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The locale to use for translations and the phone input"
                },
                "getter": false,
                "setter": false,
                "reflect": true,
                "defaultValue": "\"en-US\""
            },
            "env": {
                "type": "string",
                "attribute": "env",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Environment string to decide which endpoints to use"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"prod\""
            },
            "legacyApi": {
                "type": "boolean",
                "attribute": "legacy-api",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "deprecated",
                            "text": "only used until April 2025"
                        }],
                    "text": "use onprem Public API instead of azure"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "disableSilentAuth": {
                "type": "boolean",
                "attribute": "disable-silent-auth",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "trumpf",
                            "text": "/ux.utils"
                        }, {
                            "name": "optional",
                            "text": undefined
                        }],
                    "text": "disable silent auth - then the application needs to supply the auth token via"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "tbdbError": {}
        };
    }
    static get events() {
        return [{
                "method": "serviceCaseSubmit",
                "name": "mfServiceCaseSubmit",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event that is fired when the form is submitted"
                },
                "complexType": {
                    "original": "CreateCaseEventDetail",
                    "resolved": "CreateCaseEventDetail",
                    "references": {
                        "CreateCaseEventDetail": {
                            "location": "local",
                            "path": "/tmp/builds/5MRsrR2d8/0/TRUMPF-corp/trumpf-ux/micro-frontends/service-calls/src/components/create-service-case/create-service-case.tsx",
                            "id": "src/components/create-service-case/create-service-case.tsx::CreateCaseEventDetail"
                        }
                    }
                }
            }, {
                "method": "serviceCaseCancel",
                "name": "mfServiceCaseCancel",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event that is fired when the form is cancelled and the form should be closed"
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get watchers() {
        return [{
                "propName": "locale",
                "methodName": "onLocaleChange"
            }, {
                "propName": "prefillData",
                "methodName": "onPrefillDataChange"
            }];
    }
}
//# sourceMappingURL=create-service-case.js.map
